import React, { useMemo } from 'react';
import { FeatureFlags, Filter } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { addCustomFiltersAsExpression, cleanEmptyFilters, filterToExpression } from '@utils/filterUtils';
import { useAvContext } from '../../../context/AvContextProvider';
import { useQueryObjectAsync } from '../../../hooks/queryObjectAsync';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import {
  BarCategoryWidgetTypes,
  BarLegendPosition,
  BarSubType,
  DisplayModeOptions,
  DwQueryRequest,
  PieBasicLegendPosition,
  PieCategoryWidgetTypes,
  PieLegendStyles,
  PieSubType,
  SortDir,
  WidgetCategory,
} from '../../CustomDashboards/types';
import { filtersFieldToIgnore, TENANT_RISK_SCORE } from '../ticket.types';
import RiskScoreSeverityWidget from './RiskScoreSeverityWidget';
import { IEntityViewChartsProps, ISummarizedCollapseItem } from './types';
import { entityViewWidgetStyle, EntityViewWidgetWrapper } from './utils';

const getTenantsSeverityWidget = ({ filters }: { filters: Filter }): BarCategoryWidgetTypes => ({
  id: '',
  category: WidgetCategory.Bar,
  type: BarSubType.StackedVertical,
  definition: {
    title: { title: 'Tenants Severity' },
    coords: { h: 4, w: 6, x: 0, y: 0 },
    custom: {
      rowsColumnSwitch: false,
      legend: {
        position: BarLegendPosition.Top,
        margin: { top: 20, bottom: 10 },
      },
      fieldsPalette: {},
      displayMode: DisplayModeOptions.FIT,
    },
  },
  requests: [getTenantsSeverityRequest(filters)],
});

const getIdentityFindingsByTenantWidget = ({
  filters,
  isSmallScreen,
}: {
  filters: Filter;
  isSmallScreen: boolean;
}): PieCategoryWidgetTypes => ({
  definition: {
    title: { title: 'Identity Findings By Tenant' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [getIdentityFindingsByTenantRequest(filters)],
});

const getTenantsByIdpWidget = ({ filters, isSmallScreen }: { filters: Filter; isSmallScreen: boolean }): PieCategoryWidgetTypes => ({
  definition: {
    title: { title: 'Identity Findings by Identity Provider' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [getTenantsByIdpRequest(filters)],
});

const getTenantsByIdpRequest = (filters: Filter): DwQueryRequest => ({
  filter: filters && Object.keys(filters).length ? filters : undefined,
  select: {
    dims: [{ name: 'tenant.idp' }],
    metrics: [{ name: 'total_active_tenants', alias: 'total_active_tenants' }],
  },
  groupBy: ['tenant.idp'],
  sorting: [{ name: 'total_active_tenants', dir: SortDir.DESC }],
  top: { size: 3, groupOthers: true },
  projectionId: { name: 'uber_tenants', builtIn: true },
  distinct: false,
  metricFilterRootProj: true,
});

const getIdentityFindingsByTenantRequest = (filters: Filter): DwQueryRequest => ({
  filter: filters && Object.keys(filters).length ? filters : undefined,
  select: {
    dims: [{ name: 'tenant.name' }],
    metrics: [{ name: 'total_identity_findings', alias: 'total_identity_findings' }],
  },
  groupBy: ['tenant.name'],
  sorting: [{ name: 'total_identity_findings', dir: SortDir.DESC }],
  top: { size: 3, groupOthers: true },
  projectionId: { name: 'uber_tenants', builtIn: true },
  distinct: false,
  metricFilterRootProj: true,
});

const getTenantsSeverityRequest = (filters: Filter): DwQueryRequest => ({
  filter: filters && Object.keys(filters).length ? filters : undefined,
  select: {
    dims: [{ name: 'tenant.severity' }],
    metrics: [{ name: 'total_active_tenants', alias: 'total_active_tenants' }],
  },
  groupBy: ['tenant.severity'],
  sorting: [{ name: 'total_active_tenants', dir: SortDir.DESC }],
  top: { size: 3, groupOthers: true },
  projectionId: { name: 'uber_tenants', builtIn: true },
  distinct: false,
  metricFilterRootProj: true,
});

const TenantsViewCharts: React.FC<IEntityViewChartsProps> = ({ filters, isExpanded = true, setIsExpanded, searchFields }) => {
  const { measurements, accountEntities, featureFlags } = useAvContext();
  const isNewRiskScoreEnabled = !!featureFlags[FeatureFlags.NewRiskScore];
  const { fieldTypeMap } = accountEntities;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1501));
  const filteredFilters = Object.keys(filters).reduce(
    (acc, field) =>
      !filtersFieldToIgnore.includes(field) && !!filters[field].length
        ? {
            ...acc,
            [field]: filters[field],
          }
        : acc,
    {}
  );

  const regularFilters = filterToExpression({ ...fieldTypeMap, ...measurements.asObject }, filteredFilters);

  const customFilters = addCustomFiltersAsExpression({
    expressionFiltersObj: cleanEmptyFilters({ and: { operands: [regularFilters] } }),
    operand: 'or',
    operator: 'contains',
    fields: searchFields,
    value: filters?.search?.[0],
    fieldTypeMap,
  });

  const tenantsByIdpWidget = getTenantsByIdpWidget({ filters: customFilters, isSmallScreen });
  const tenantsSeverityWidget = getTenantsSeverityWidget({ filters: customFilters });
  const identityFindingsByTenantWidget = getIdentityFindingsByTenantWidget({ filters: customFilters, isSmallScreen });

  const widgetsToShow = [tenantsSeverityWidget, identityFindingsByTenantWidget, tenantsByIdpWidget];

  const summerizeByIdp = (data: any[], labelKey = 'tenant.idp', valueKey = 'total_active_tenants') =>
    data.reduce(
      (acc, currentValue) => ({
        ...acc,
        [currentValue[labelKey]]: currentValue[valueKey],
      }),
      {}
    );

  const { data: tenantsByIDP, isLoading: tenantsByIDPIsLoading } = useQueryObjectAsync({
    queryObject: [getTenantsByIdpRequest(customFilters)].map(request => ({
      ...request,
      top: { ...request.top, size: 3 },
    })) as DwQueryRequest[],
    options: { placeholderData: keepPreviousData },
    onSuccess: data => summerizeByIdp(data),
  });

  const summaryItems: ISummarizedCollapseItem[] = useMemo(
    () => [
      {
        title: 'Tenants by Identity Provider',
        data: tenantsByIDP,
        isLoading: tenantsByIDPIsLoading,
        showPills: true,
      },
    ],
    [tenantsByIDP, tenantsByIDPIsLoading]
  );

  return (
    <EntityViewWidgetWrapper isExpanded={isExpanded} setIsExpanded={setIsExpanded} summaryItemsProps={{ items: summaryItems }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ ...flex.row, gap: 1.5, height: '208px' }}>
          {widgetsToShow.map(widget => (
            <Box
              key={widget.definition.title.title}
              sx={{
                ...entityViewWidgetStyle,
                position: 'relative',
                backgroundColor: theme => theme.palette.white.main,
              }}>
              {isNewRiskScoreEnabled && widget === tenantsSeverityWidget ? (
                <RiskScoreSeverityWidget widget={widget} riskScoreField={TENANT_RISK_SCORE} severityField="tenant.severity" />
              ) : (
                <DraggableWidget
                  widget={widget}
                  customToolbar={
                    <Typography sx={{ whiteSpace: 'nowrap', marginBottom: '8px' }} variant="h6">
                      {widget.definition.title.title}
                    </Typography>
                  }>
                  {renderWidget({ widget })}
                </DraggableWidget>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default TenantsViewCharts;
